/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { AbList } from "abreact/input/AbList";
import { $createPersonNode } from "abreact/input/markdown/nodes/PersonNode";
import { TypeaheadMenuPlugin } from "abreact/input/markdown/plugins/TypeaheadMenuPlugin";
import { useOnMount } from "abreact/utils/AbHooks";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { $createTextNode, $insertNodes, COMMAND_PRIORITY_LOW, KEY_DOWN_COMMAND } from "lexical";
import React from "react";
import { getNoPersonMatches, getPersonExtension, standard_protocols } from "abreact/input/markdown/utils/MarkdownUtils";
import { URLUtils } from "abweb/utils/URLUtils";
import { $createAbLinkNode } from "abreact/input/markdown/nodes/AbLinkNode";
import { $createLinkNode } from "@lexical/link";
const PersonRegex = /@(.*)$/;
const personsCache = new Map();
const LOG = LogUtils.getLogger(__filename);
function usePersonExtensionMatching(nameSoFar) {
  const [results, setResults] = React.useState([]);
  React.useEffect(() => {
    const personExtension = getPersonExtension();
    if (personExtension === null) {
      setResults([]);
      return;
    }
    if (nameSoFar == null) {
      setResults([getNoPersonMatches()]);
      return;
    }
    const cachedResults = personsCache.get(nameSoFar);
    if (cachedResults === null) {
      return;
    } else if (cachedResults !== undefined) {
      setResults(cachedResults);
      return;
    }
    personsCache.set(nameSoFar, []);
    try {
      personExtension.getMatchingPersons(nameSoFar).then(matchingPersons => {
        personsCache.set(nameSoFar, matchingPersons);
        setResults(matchingPersons);
      });
    } catch (e) {
      // eslint-disable-next-line abinitio/no-instanceof
      LOG.error("Markdown Editor: getMatchingPersons() failed:" + (e instanceof Error ? e.message : String(e)));
      setResults([]);
    }
  }, [nameSoFar]);
  return results;
}
export default function PersonPlugin() {
  const [editor] = useLexicalComposerContext();
  const [nameSoFar, setNameSoFar] = React.useState(null);
  const results = usePersonExtensionMatching(nameSoFar);
  const [lastKey, setLastKey] = React.useState(null);
  const [showPopup, setShowPopup] = React.useState(false);
  const [selection, setSelection] = React.useState(undefined);
  const [selectionIndex, setSelectionIndex] = React.useState(0);
  const [handledKey, setHandledKey] = React.useState(false);
  useOnMount(() => {
    return editor.registerCommand(KEY_DOWN_COMMAND, event => {
      setLastKey(event.key);
      setHandledKey(false);
      return false;
    }, COMMAND_PRIORITY_LOW);
  });
  const options = React.useMemo(() => {
    setSelection(results[0]);
    setSelectionIndex(0);
    return results;
  }, [results]);

  // handle the up and down arrow keys
  React.useEffect(() => {
    if (!handledKey && (lastKey === "ArrowDown" || lastKey === "ArrowUp")) {
      setHandledKey(true);
      const increment = lastKey === "ArrowDown" ? 1 : -1;
      const newIndex = selectionIndex + increment;
      if (newIndex < 0 || newIndex >= results.length) return; // can't increment past the end of the filter
      setSelectionIndex(newIndex); // set our state
      setSelection(results[newIndex]); // keep selection and selectionIndex in sync
    }
  }, [lastKey, results, handledKey, selectionIndex]);

  // handle the select person action
  const onSelectOption = React.useCallback((selectedOption, nodeToReplace, closeMenu) => {
    if (selectedOption === getNoPersonMatches()) return; // that special person is not a link

    editor.update(() => {
      const personExtension = getPersonExtension();
      let nodeToInsert;
      if (personExtension?.markdownToInsert) {
        // extension has its own markdown
        let {
          displayName,
          url
        } = personExtension.markdownToInsert(selectedOption);
        let protocol = URLUtils.getProtocol(url);
        if (protocol === undefined) {
          // can't parse, use "person" protocol
          protocol = "person";
          url = "person:" + url;
        }
        if (protocol === "person") {
          nodeToInsert = $createPersonNode(url, displayName);
        } else if (standard_protocols.has(protocol)) {
          const linkNode = $createLinkNode(url);
          const linkTextNode = $createTextNode(displayName);
          linkNode.append(linkTextNode);
          nodeToInsert = linkNode;
        } else {
          // non-standard protocol, use abLink node.
          const linkNode = $createLinkNode(url);
          const abLinkNode = $createAbLinkNode(displayName, url);
          linkNode.append(abLinkNode);
          nodeToInsert = linkNode;
        }
      } else {
        // either no extension or extension has no markdown, use person node.
        nodeToInsert = $createPersonNode(selectedOption.id, selectedOption.displayName);
      }
      if (nodeToReplace) {
        nodeToReplace.replace(nodeToInsert);
      } else {
        // insert at selection
        $insertNodes([nodeToInsert]);
      }
      nodeToInsert.selectEnd(); // move selection to after insertion
      closeMenu();
    });
  }, [editor]);
  const checkForPersonMatch = text => {
    if (lastKey === "@") {
      setShowPopup(true);
    } else if (!showPopup) {
      setSelection(undefined);
      return null;
    }
    let match = PersonRegex.exec(text);
    if (match !== null) {
      return {
        leadOffset: match.index,
        matchingString: match[1],
        replaceableString: match[0]
      };
    }
    return null;
  };
  const itemRenderer = person => {
    if (person.id === getNoPersonMatches().id) {
      // the no-person-matches person has a special renderer
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
        className: "choice-no-person-matches ab-italic"
      }, person.displayName));
    }
    return getPersonExtension()?.renderChoice(person, nameSoFar || undefined) ??
    /*#__PURE__*/
    // The extension gets preference as renderer, but we have a backup.
    React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "choice-display-name"
    }, person.displayName), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("span", {
      className: "choice-id"
    }, person.id));
  };
  const menuRenderFn = (anchorElementRef, {
    selectedIndex,
    selectOptionAndCleanUp,
    setHighlightedIndex
  }) => {
    return anchorElementRef.current && results.length ? /*#__PURE__*/React.createElement(AbPopup, {
      className: "ab-markdown-person-popup",
      origin: anchorElementRef.current ?? undefined,
      positioner: PopupUtils.VERTICAL_FIXED_SIZE_POSITIONER,
      flushContent: true,
      overlay: false,
      onClose: () => setShowPopup(false),
      onBackgroundCaptureMouseDown: () => setShowPopup(false)
    }, /*#__PURE__*/React.createElement(AbList, {
      items: options,
      selectionMode: SelectionMode.SINGLE,
      itemRenderer: itemRenderer,
      onFocusChange: value => value && selectOptionAndCleanUp(value),
      scrollToIndex: selectedIndex ?? undefined,
      value: selection,
      onChange: item => {
        setSelection(item);
        const index = options.indexOf(item);
        setSelectionIndex(index);
        setHighlightedIndex(index);
      }
    })) : null;
  };
  return /*#__PURE__*/React.createElement(TypeaheadMenuPlugin, {
    options: options,
    onQueryChange: setNameSoFar,
    onSelectOption: onSelectOption,
    triggerFn: checkForPersonMatch,
    menuRenderFn: menuRenderFn
  });
}
//# sourceMappingURL=PersonPlugin.js.map
